import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as moment from 'moment-timezone';
import { ReplaySubject } from 'rxjs';
import Swal from 'sweetalert2';

import { User } from './../interfaces/user.model';
import { FormControl } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isLogged = new ReplaySubject<boolean>();

  /**
   * Observable que se actualiza cada vez que hay un login o logout o se consulta el estado de login.
   * Es útil subscribirse a este observable en un navbar por ejemplo.
   * Puede ser utilizado con un subscribe() desde un component o desde el template con el pipe async.
   */
  public logged$ = this.isLogged.asObservable();

  userToken: string;
  userUser: string;
  userLogged: boolean;

  constructor(private http: HttpClient, private router: Router, private jwtHelper: JwtHelperService) {
    this.leerToken();
    this.estaAutenticado();
  }

  // Registro de usuario
  // -----------------------------------------------------

  registro(usuario: User) {
    let authData: User = {
      ...usuario,
      role: 1,
    };

    return new Promise((resolve) => {
      this.http.post(environment.apiEndpoint + '/Account/Register', authData).subscribe((res) => {
        if (res) {
          Swal.fire({
            icon: 'success',
            title: 'Bien!',
            text: 'Te envimos un correo con instrucciones a ' + usuario.email + ' para confirmar la cuenta.',
            confirmButtonText: 'ACEPTAR',
            showConfirmButton: true,
            customClass: { popup: 'auth_register_mail_success' },
            didClose: () => resolve(true),
          });
        } else {
          Swal.fire(
            'Su cuenta ya está registrada',
            'Parece que el correo ' + usuario.email + ' ya se encuentra registrado.',
            'warning',
          );
          resolve(false);
        }
      });
    });
  }

  confirmarRegistro(authData) {
    return new Promise((resolve) => {
      this.http.post(`${environment.apiEndpoint}/Account/ConfirmAccount`, authData).subscribe(
        (res) => {
          if (res) {
            // Notifica que la confirmación es exitosa y retorna para atrás
            Swal.fire({
              icon: 'success',
              title: 'Bien!',
              html: `Se registró la cuenta ${authData.email} correctamente. <a href='/login'> Iniciar sesión </a>`,
              showConfirmButton: true,
              confirmButtonText: 'ENTENDIDO',
              customClass: { popup: 'auth_register_success' },
              didClose: () => {
                this.router.navigate(['login']);
                resolve(true);
              },
            });
          } else {
            // Notifica que expiró el token y redirige a registro
            Swal.fire({
              icon: 'warning',
              title: 'Tiempo expirado',
              html: 'El tiempo para confirmar la cuenta ha expirado. <br><br> Vuelva a registrar la cuenta.',
              showConfirmButton: true,
              confirmButtonText: 'ENTENDIDO',
              timer: 1000,
              didClose: () => {
                this.router.navigate(['registro']);
                resolve(false);
              },
            });
          }
        },
        (error) => {
          Swal.fire(
            'UPS!',
            'Ha ocurrido un error al intentar confirmar la cuenta. <br><br> Intente nuevamente en unos minutos.',
            'error',
          );
          resolve(false);
        },
      );
    });
  }

  // Login y Logout
  // -----------------------------------------------------

  login(email, password) {
    return new Promise((resolve) => {
      let authData = {
        userName: email,
        password: password,
      };

      this.http.post(environment.apiEndpoint + '/Account/Login', authData).subscribe(
        (res) => {
          if (res['result']) {
            this.guardarToken(res['token'], authData.userName);

            this.isLogged.next(true);
            this.userLogged = (true);


            Swal.fire({
              icon: 'success',
              title: 'Bienvenido/a!',
              showConfirmButton: false,
              timer: 1000,
              customClass: { popup: 'auth_login_success' },
              didClose: () => {
                this.userToken = res['token'];
                this.userUser = authData.userName;
                resolve(true);
              },
            });
          } else {
            this.isLogged.next(false);
            this.userLogged = (false);

            Swal.fire(
              'Usuario o contraseña incorrecto',
              'Revise el correo o la contraseña e intente nuevamente.',
              'warning',
            );
            resolve(false);
          }
        },
        (error) => {
          Swal.fire(
            'UPS!',
            'Ha ocurrido un error al intentar ingresar al sistema. Intente nuevamente más tarde.',
            'error',
          );
          resolve(false);
        },
      );
    });
  }

  logout(redirigir = '') {
    localStorage.removeItem('cinemateca_token');
    this.logoutCaf();

    this.userUser = '';
    this.userToken = '';
    this.isLogged.next(false);
    this.userLogged = (false);


    if (redirigir) this.router.navigateByUrl(redirigir);
    else this.router.navigateByUrl('/home');
  }

  logoutNoRefresh() {
    localStorage.removeItem('cinemateca_token');
    this.logoutCaf();

    this.userUser = '';
    this.userToken = '';
    this.isLogged.next(false);
    this.userLogged = (false);
  }

  // Olvido de contraseña
  // -----------------------------------------------------

  resetearPassword(email: string) {
    return new Promise((resolve) => {
      this.http
        .post(environment.apiEndpoint + '/Account/SendResetPasswordEmail?email=' + email, {})
        .subscribe((resp) => {
          if (resp) {
            Swal.fire({
              title: 'Revise su correo!',
              text: `Hemos enviado un correo con instrucciones para restaurar la contraseña a la casilla ${email}.`,
              icon: 'success',
              didClose: () => {
                this.router.navigateByUrl('/home');
              },
            });
            resolve(true);
          } else {
            Swal.fire(
              'Su cuenta no existe',
              'Parece que el correo ' + email + ' no se encuentra registrada.',
              'warning',
            );
            resolve(false);
          }
        });
    });
  }

  confirmarResetearPassword(authData) {
    return new Promise((resolve) => {
      this.http.post(environment.apiEndpoint + '/Account/ResetPassword', authData).subscribe(
        (res) => {
          if (res) {
            Swal.fire(
              'Bien!',
              `Se actualizó la contraseña de la cuenta ${authData.email} correctamente. <a href='/login'> Iniciar sesión </a>`,
              'success',
            );
            resolve(true);
          } else {
            Swal.fire(
              'Tiempo expirado',
              'El tiempo para confirmar el reseteo de la contraseña ha expirado. ',
              'warning',
            );
            resolve(false);
          }
        },
        (error) => {
          Swal.fire(
            'UPS!',
            'Ha ocurrido un error al intentar resetear la cuenta! <br><br> Intente nuevamente en unos minutos.',
            'error',
          );
          resolve(false);
        },
      );
    });
  }

  // Cambio consciente de contraseña
  // -----------------------------------------------------

  cambioPasswordConsciente(authData) {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${this.leerToken().token}`);

    let body = {
      currentPassword: authData.password0,
      newPassword: authData.password1,
      passwordConfirm: authData.password2,
    };

    return new Promise((resolve) => {
      this.http
        .post(environment.apiEndpoint + '/Account/ChangeKnownPassword', body, {
          headers,
        })
        .subscribe(
          (res) => {
            if (res) {
              Swal.fire(
                'Bien!',
                `Se actualizó la contraseña de la cuenta ${authData.email} correctamente. `,
                'success',
              );
              resolve(true);
            } else {
              Swal.fire('Contraseña incorrecta', 'Verifique la contraseña actual e intente nuevamente ', 'warning');
              resolve(false);
            }
          },
          (error) => {
            Swal.fire('Error en la sesión', 'Ingrese nuevamente para cambiar la contraseña. ', 'warning');
            resolve(false);
          },
        );
    });
  }

  // Editar usuario
  // -----------------------------------------------------

  // obtenerDatosPersonales() {
  //   return new Promise((resolve) => {
  //     let headers = new HttpHeaders().set('Authorization', `Bearer ${this.leerToken().token}`);

  //     this.http.get(environment.apiEndpoint + '/Account/Gets', { headers }).subscribe((res) => {
  //       resolve(res);
  //     });
  //   });
  // }

  enviarNuevosDatosPersonales(usuario: User) {
    let authData: User = {
      ...usuario,
      role: 1,
    };

    return new Promise((resolve) => {
      let headers = new HttpHeaders().set('Authorization', `Bearer ${this.leerToken().token}`);

      this.http.post(environment.apiEndpoint + '/Account/Update', authData, { headers }).subscribe((res) => {
        if (res) {
          Swal.fire({
            icon: 'success',
            title: 'Bien!',
            text: 'Se actualizaron los datos del usuario ' + usuario.email + ' correctamente.',
            confirmButtonText: 'ACEPTAR',
            showConfirmButton: true,
            didClose: () => resolve(true),
          });
        } else {
          Swal.fire(
            'UPS!',
            'Parece que ha ocurrido un error al actualizar los datos de ' + usuario.email + '.',
            'warning',
          );
          resolve(false);
        }
      });
    });
  }

  // Helpers
  // -----------------------------------------------------

  //Referencia: http://blog.enriqueoriol.com/2017/05/comunicacion-servicio-componente-en-angular.html

  /**
   * Retorna `true` si existe token en LocalStorage.
   * Si el resultado es `true`, se redirecciona a lo que haya en `redirigir` en caso de haber valor.
   * @param redirigir Ruta a ridereccionar si está logueado. Por defecto está vacío.
   */
  estaAutenticado(redirigir = ''): boolean {
    if (localStorage.getItem('cinemateca_token')) {
      const token = JSON.parse(localStorage.getItem('cinemateca_token'))['token'];
      if (token) {
        let expDate: Date = this.jwtHelper.getTokenExpirationDate(token);

        let fechaLocal = moment(moment().tz('America/Montevideo')['_d']).format();
        // let fechaLocal = Date.now();

        let diferencia = moment(expDate).diff(fechaLocal);

        if (diferencia > 0) {
          if (redirigir) {
            this.router.navigateByUrl(redirigir);
          }
          this.isLogged.next(true);
          this.userLogged = true;
          return true;
        }
      }
      this.logoutNoRefresh();
    }

    this.isLogged.next(false);
    this.userLogged = false;
    return false;
  }

  /**
   * Retorna `true` si NO existe token en LocalStorage.
   * Si el resultado es `true`, se redirecciona a lo que haya en `redirigir` en caso de haber valor.
   * @param redirigir Ruta a ridereccionar si NO está logueado. Por defecto está vacío.
   */
  noEstaAutenticado(redirigir = ''): boolean {
    const token = localStorage.getItem('cinemateca_token');
    this.isLogged.next(token ? true : false);
    this.userLogged = (token ? true : false);


    if (!token && redirigir) {
      this.router.navigateByUrl(redirigir);
    }

    return !token ? true : false;
  }

  getEmail() {
    return this.userUser;
  }

  getUserLogged(): boolean {
    return this.userLogged;
  }

  /**
   * Compara 2 campos de Password. Es obligatorio utilizar este validator en la segunda Password.
   * Tambien es importante utilizar de nombre de los controles 'password1' y 'password2'.
   */
  compararPassword(control: FormControl): { [s: string]: boolean } {
    let forma: any = this;

    if (control.value !== forma.controls['password1'].value) {
      return {
        noiguales: true,
      };
    }
    return null;
  }

  leerToken() {
    if (localStorage.getItem('cinemateca_token')) {
      let regToken = JSON.parse(localStorage.getItem('cinemateca_token'));

      this.userToken = regToken['token'];
      this.userUser = regToken['user'];

      return regToken;
    } else {
      this.userToken = '';
    }

    return this.userToken;
  }

  /**
   * Indica si la IP es válida para usar la plataforma en el país
   * @returns boolean
   */
  ipValid(movieId): Promise<boolean> {
    return new Promise((resolve) => {
      // En desarrollo no se valida la IP
      // if (!environment.production) return resolve(true);

      http: this.http.get(`${environment.apiEndpoint}/Movie/ValidIpMovie/${movieId}`).subscribe(


        (res) => resolve(true),
        (error) => resolve(false));
    });
  }


  //#region Caf
  getCafToken(): string | null {
    let cafToken = localStorage.getItem('cinemateca_cafToken');

    return cafToken ? cafToken : null;
  }

  loginCaf(password): Promise<boolean> {
    return new Promise(resolve => {

      const headers = new HttpHeaders().set(
        'Authorization',
        `Bearer ${JSON.parse(localStorage.getItem('cinemateca_token'))['token']}`,
      );

      this.http.post(environment.apiEndpoint + '/home/sectionLogin', { password }, { headers }).subscribe((res: respTokenCaf) => {

        if (res.result) {
          Swal.fire({
            icon: 'success',
            title: 'Bienvenido/a!',
            showConfirmButton: false,
            timer: 1000,
            customClass: { popup: 'auth_login_caf_success' },
            didClose: () => {
              localStorage.setItem("cinemateca_cafToken", res.token)

              resolve(true);
            },
          });
        } else {
          Swal.fire(
            'Contraseña incorrecta',
            "Intente nuevamente o comuníquese con el administrador",
            'warning',
          );

          resolve(false);
        }

      });
    })
  }

  logoutCaf() {
    localStorage.removeItem("cinemateca_cafToken")
  }

  //#endregion
  // ---------------------------------------------------------------

  private guardarToken(xToken: string, usuario: string) {
    this.userToken = xToken;

    let tok = {
      token: this.userToken,
      user: usuario,
    };

    localStorage.setItem('cinemateca_token', JSON.stringify(tok));
  }
}

interface respTokenCaf {
  result: boolean,
  token: string
}